import { useEffect, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { GridPaginationModel } from "@mui/x-data-grid";

interface PageInfo {
  endCursor?: string | null;
  hasNextPage?: boolean;
}

/** Helper for prefixed URL param keys */
const getParamKey = (prefix: string | undefined, key: string) =>
  prefix ? `${prefix}.${key}` : key;

export interface UseDataGridPaginationOptions {
  /** Prefix for URL params (e.g., "assets" → "assets.after", "assets.pageSize") */
  prefix?: string;

  /** Page size on initial render. Default: 10 */
  defaultPageSize?: number;

  /** PageInfo from your GraphQL query result */
  pageInfo: PageInfo | undefined;

  /** Total count from your GraphQL query result */
  totalCount: number | undefined;

  /** Pagination resets when any value in this array changes */
  resetDeps?: unknown[];
}

export interface UseDataGridPaginationResult {
  /** Pass directly to DataGrid's paginationModel prop */
  paginationModel: GridPaginationModel;

  /** Pass directly to DataGrid's onPaginationModelChange prop */
  onPaginationModelChange: (model: GridPaginationModel) => void;

  /** Pass directly to DataGrid's rowCount prop */
  rowCount: number;

  /** Pass to your GraphQL query's first variable */
  first: number;

  /** Pass to your GraphQL query's after variable */
  after: string | undefined;

  /** Whether there's a next page available */
  hasNextPage: boolean;

  /** Navigate to next page (updates URL with cursor) */
  goToNextPage: () => void;

  /** Reset pagination (clears cursor from URL) */
  resetPagination: () => void;
}

export function useDataGridPagination({
  prefix,
  defaultPageSize = 10,
  pageInfo,
  totalCount,
  resetDeps = [],
}: UseDataGridPaginationOptions): UseDataGridPaginationResult {
  const [searchParams, setSearchParams] = useSearchParams();

  // URL param keys (with optional prefix)
  const afterParamKey = getParamKey(prefix, "after");
  const pageSizeParamKey = getParamKey(prefix, "pageSize");
  const pageIndexParamKey = getParamKey(prefix, "page");

  // Read pagination state from URL
  const afterParam = searchParams.get(afterParamKey) ?? undefined;
  const pageSizeParam = searchParams.get(pageSizeParamKey);
  const pageSize = pageSizeParam ? parseInt(pageSizeParam) : defaultPageSize;
  const pageIndexParam = searchParams.get(pageIndexParamKey);
  const pageIndex = pageIndexParam ? parseInt(pageIndexParam) : 0;

  // Use actual page index from URL (supports Apollo cache slicing)
  const paginationModel: GridPaginationModel = useMemo(
    () => ({
      page: pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize],
  );

  // Memoized row count to prevent DataGrid resetting during loading
  // See: https://mui.com/x/react-data-grid/pagination/#server-side-pagination
  const rowCountRef = useRef(0);
  const rowCount = useMemo(() => {
    if (totalCount !== undefined) {
      rowCountRef.current = totalCount;
    }
    return rowCountRef.current;
  }, [totalCount]);

  // Memoized pageInfo to prevent navigation breaking during cache-and-network flicker
  const pageInfoRef = useRef<PageInfo | undefined>(undefined);
  if (pageInfo) {
    pageInfoRef.current = pageInfo;
  }
  const stablePageInfo = pageInfoRef.current;

  // Track if this is the first render to avoid clearing URL on mount
  const isFirstRender = useRef(true);

  // Reset pagination when dependencies change (but not on first render)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete(afterParamKey);
        newParams.delete(pageIndexParamKey);
        return newParams;
      },
      { replace: true },
    );
  }, resetDeps);

  // Handle pagination model changes (page changes and page size changes)
  const onPaginationModelChange = useCallback(
    (newModel: GridPaginationModel) => {
      if (newModel.pageSize !== pageSize) {
        // Page size changed: update URL and reset cursor + page
        setSearchParams(
          (prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.set(pageSizeParamKey, newModel.pageSize.toString());
            newParams.delete(afterParamKey);
            newParams.delete(pageIndexParamKey);
            return newParams;
          },
          { replace: true },
        );
      } else if (newModel.page > pageIndex) {
        // User clicked next page - use endCursor to navigate forward
        const endCursor = stablePageInfo?.endCursor;
        if (endCursor && stablePageInfo?.hasNextPage) {
          setSearchParams(
            (prev) => {
              const newParams = new URLSearchParams(prev);
              newParams.set(afterParamKey, endCursor);
              newParams.set(pageIndexParamKey, newModel.page.toString());
              return newParams;
            },
            { replace: true },
          );
        }
      } else if (newModel.page < pageIndex) {
        // User clicked previous page - data is in cache, just update page index
        if (newModel.page === 0) {
          // Going back to first page - clear cursor and page index
          setSearchParams(
            (prev) => {
              const newParams = new URLSearchParams(prev);
              newParams.delete(afterParamKey);
              newParams.delete(pageIndexParamKey);
              return newParams;
            },
            { replace: true },
          );
        } else {
          // Going back to intermediate page - keep cursor, update page index
          // Cache has all previous pages, we just slice differently
          setSearchParams(
            (prev) => {
              const newParams = new URLSearchParams(prev);
              newParams.set(pageIndexParamKey, newModel.page.toString());
              return newParams;
            },
            { replace: true },
          );
        }
      }
    },
    [
      pageSize,
      pageIndex,
      setSearchParams,
      pageSizeParamKey,
      afterParamKey,
      pageIndexParamKey,
      stablePageInfo,
    ],
  );

  // Navigate to next page by setting cursor and page index in URL
  const goToNextPage = useCallback(() => {
    const endCursor = stablePageInfo?.endCursor;
    if (endCursor && stablePageInfo?.hasNextPage) {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set(afterParamKey, endCursor);
          newParams.set(pageIndexParamKey, (pageIndex + 1).toString());
          return newParams;
        },
        { replace: true },
      );
    }
  }, [
    stablePageInfo,
    setSearchParams,
    afterParamKey,
    pageIndexParamKey,
    pageIndex,
  ]);

  // Reset pagination (clear cursor and page index from URL)
  const resetPagination = useCallback(() => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete(afterParamKey);
        newParams.delete(pageIndexParamKey);
        return newParams;
      },
      { replace: true },
    );
  }, [setSearchParams, afterParamKey, pageIndexParamKey]);

  return {
    paginationModel,
    onPaginationModelChange,
    rowCount,
    first: pageSize,
    after: afterParam,
    hasNextPage: stablePageInfo?.hasNextPage ?? false,
    goToNextPage,
    resetPagination,
  };
}
